import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import Tooltip from "@edaz/edazkit/Tooltip";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist";
import { DataTable } from "@frui.ts/dataviews/dist";
import { registerView } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views/dist";
import { View } from "@frui.ts/views/dist";
import type ChargeResponse from "entities/chargeResponse";
import { observer, Observer } from "mobx-react-lite";
import React from "react";
import { formatDate, formatDateTime, formatLicensePlate, translateGeneral } from "utils/localizations";
import ChargesViewModel from "viewModels/callCenter/search/charges/chargesViewModel";
import { chargeDisplayStatusToVariant } from "entities/chargeDisplayStatus";
import { chargeOrderStatusToVariant } from "entities/chargeOrderStatus";
import { refundDisplayStatusVariant } from "../../../../entities/refundDisplayStatus";
import ChargeActionMenu from "./chargeActionMenu";
import Paginator from "views/common/paginator";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import classNames from "classnames";
import { emptyValue } from "utils/emptyValue";

const ChargesView: ViewComponent<ChargesViewModel> = observer(({ vm }) => {
  const headerClassName = "py-4 text-wrap align-middle";
  const wrapClassName = "text-wrap";
  const noWrapClassName = "text-nowrap";

  const columns: ColumnDefinition<ChargeResponse>[] = [
    {
      title: vm.translateAttribute("vehicle"),
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: ({ item }) => {
        const newLicensePlate = item.vehicle.licensePlate;
        const originalLicensePlate = vm.filter.licensePlate;

        const licensePlateChangedInformation = originalLicensePlate && newLicensePlate !== originalLicensePlate;

        if (item.wasVehicleChangedDuringValidity || licensePlateChangedInformation) {
          const tooltips = [];

          if (item.wasVehicleChangedDuringValidity) {
            tooltips.push(vm.translate("changesAfterValidityTooltip"));
          }

          if (licensePlateChangedInformation) {
            const submissionDateText = item.vehicleRegistrationChangeSubmissionDate
              ? vm
                  .translate("submissionDate")
                  .replace("%date%", formatDate(item.vehicleRegistrationChangeSubmissionDate) || emptyValue)
              : "";

            const licensePlateChangeTooltip = vm
              .translate("licensePlateChanged")
              .replace("%originalLicensePlate%", originalLicensePlate)
              .replace("%newLicensePlate%", newLicensePlate)
              .replace("%submissionDate%", submissionDateText);

            tooltips.push(licensePlateChangeTooltip);
          }

          const tooltip = tooltips.join(" ");

          return (
            <Tooltip id="license-plate-tooltip" className="license-plate-tooltip" tooltip={tooltip}>
              <span
                className={classNames({
                  "license-plate-warning": item.wasVehicleChangedDuringValidity,
                  "font-weight-bold text-secondary link-underline text-nowrap": licensePlateChangedInformation,
                })}>
                <span>{formatLicensePlate(item.vehicle)}</span>
                {item.wasVehicleChangedDuringValidity && <Icon icon={IconType.CircleWarning} width={22} height={22} />}
              </span>
            </Tooltip>
          );
        }

        return formatLicensePlate(item.vehicle);
      },
    },
    {
      title: vm.translateAttribute("chargeOrderCreatedAt"),
      property: "chargeOrderCreatedAt",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("chargeOrderConfirmedAt"),
      property: "chargeOrderConfirmedAt",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("chargeTypeId"),
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => vm.translateChargeType(props.item) ?? emptyValue,
    },
    {
      title: vm.translateAttribute("price"),
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => vm.translatePrice(props.item) ?? emptyValue,
    },
    {
      title: vm.translateAttribute("fuelTypeId"),
      property: "fuelTypeId",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => vm.translateFuelType(props.item) ?? emptyValue,
    },
    {
      title: vm.translateAttribute("validSince"),
      property: "validSince",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("validUntil"),
      property: "validUntil",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
    {
      title: vm.translateAttribute("orderIdent"),
      property: "orderIdent",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      title: vm.translate("purchased"),
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => vm.translatePurchased(props.item) ?? emptyValue,
    },
    {
      title: vm.translateAttribute("emailAddress"),
      property: "emailAddress",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      title: vm.translateAttribute("notificationPhoneNumber"),
      property: "notificationPhoneNumber",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      title: vm.translateAttribute("officialRecordId"),
      property: "officialRecordId",
      headerClassName: headerClassName,
      cellClassName: noWrapClassName,
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      title: vm.translateAttribute("orderStatus"),
      property: "orderStatus",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => {
        if (!props.item.orderStatus) {
          return emptyValue;
        }

        return (
          <strong className={`text-${chargeOrderStatusToVariant(props.value)}`}>
            {vm.translateChargeOrderStatus(props.value)}
          </strong>
        );
      },
    },
    {
      title: vm.translateAttribute("chargeDisplayStatus"),
      property: "chargeDisplayStatus",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => {
        if (!props.item.chargeDisplayStatus) {
          return emptyValue;
        }

        return (
          <strong className={`text-${chargeDisplayStatusToVariant(props.value)}`}>
            {vm.translateChargeDisplayStatus(props.value)}
          </strong>
        );
      },
    },
    {
      title: vm.translateAttribute("refundDisplayStatus"),
      property: "refundDisplayStatus",
      headerClassName: headerClassName,
      cellClassName: wrapClassName,
      valueFormatter: props => {
        if (!props.item.refundDisplayStatus) {
          return emptyValue;
        }

        return (
          <strong className={`text-${refundDisplayStatusVariant(props.value)}`}>
            {vm.translateChargeRefundDisplayStatus(props.value, props.item.isPartiallyRefunded ?? false)}
          </strong>
        );
      },
    },
    {
      cellClassName: "text-align-end",
      headerClassName: headerClassName,
      valueFormatter: props => <ChargeActionMenu vm={vm} charge={props.item} />,
    },
  ];

  return (
    <>
      <LoaderOverlay loading={vm.loading}>
        <DataTable
          headerRowClassName="bg-white"
          columns={columns}
          items={vm.items}
          rowProps={item => {
            if (item === vm.openItem) {
              return {
                className: "table-active",
              };
            }
            return {
              className: vm.rowColor(item.orderStatus, item.chargeDisplayStatus),
            };
          }}
          itemKey="chargeId"
          className="mt-n4 table"
        />
        {vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("loading")}</div>
        )}
        {!vm.loading && vm.items.length === 0 && (
          <div className="bg-light mt-n4 mb-4 px-4 py-3 border-bottom text-muted">{translateGeneral("empty")}</div>
        )}
        <Paginator
          className="justify-content-center mt-4 mb-2"
          pagingInfo={vm.currentPaging}
          filter={vm.pagingFilter}
          onPageChanged={() => vm.loadData()}
        />
      </LoaderOverlay>
      <Observer>{() => <View vm={vm.activeChild} />}</Observer>
    </>
  );
});

registerView(ChargesView, ChargesViewModel);
